import React, { useContext, useState, useEffect, useCallback, useMemo } from "react"
import { firestore } from '../firebase/firebase'
import { auth } from "../firebase/firebase"

const AuthContext = React.createContext()

const profileCacheKey = (uid) => `gtAuthProfile.${uid}`
const isPlainValue = (value) => value === null || ['string', 'number', 'boolean'].includes(typeof value)

const readProfileCache = (uid) => {
  try {
    const cached = JSON.parse(window.localStorage.getItem(profileCacheKey(uid)));
    return cached && cached.userInfo && Array.isArray(cached.roles) ? cached : null;
  } catch (e) { return null; }
}

// Only JSON-safe fields are cached; Timestamps and nested maps wait for the live listener.
const writeProfileCache = (uid, userInfo, roles) => {
  const safeInfo = {};
  Object.entries(userInfo).forEach(([key, value]) => {
    if (isPlainValue(value) || (Array.isArray(value) && value.every(isPlainValue))) safeInfo[key] = value;
  });
  try { window.localStorage.setItem(profileCacheKey(uid), JSON.stringify({ userInfo: safeInfo, roles })); } catch (e) { /* storage unavailable */ }
}

const clearProfileCache = (uid) => {
  try { window.localStorage.removeItem(profileCacheKey(uid)); } catch (e) { /* storage unavailable */ }
}

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {

  const [currentUser, setCurrentUser] = useState(null);
  const [userSettings, setUserSettings] = useState({})
  const [userInfo, setUserInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesResolvedFor, setRolesResolvedFor] = useState(null);

  const signup = useCallback((email, password) => {
    return auth.createUserWithEmailAndPassword(email, password)
  }, [])

  const login = useCallback((email, password) => {
    return auth.signInWithEmailAndPassword(email, password);
  }, [])

  const logout = useCallback(() => {
    return auth.signOut()
  }, [])

  const resetPassword = useCallback((email) => {
    return auth.sendPasswordResetEmail(email)
  }, [])

  const updateEmail = useCallback((email) => {
    return currentUser.updateEmail(email)
  }, [currentUser])

  const updatePassword = useCallback((password) => {
    return currentUser.updatePassword(password)
  }, [currentUser])

  const updateUserSettings = useCallback((field, value) => {
    firestore.collection('userSettings').doc(currentUser.uid)
    .set({[field]: value}, { merge: true })
    .then(() => { setUserSettings((prevUserSettings) => ({...prevUserSettings, [field]: value} || {})) })
    .catch((e) => {console.warn(e);});
  }, [currentUser])

  // Track the authenticated firebase user. The data listeners below react to it.
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user !== null) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setUserInfo({});
        setRoles([]);
        setRolesResolvedFor(null);
        setSettingsLoaded(false);
        setLoading(false);
      }
      setAuthLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  // Live listener on the signed-in user's OWN users document, so a role
  // reassignment (or any profile change) propagates without a refresh.
  // userSettings is a one-time read keyed to the same user.
  useEffect(() => {
    if (!currentUser) {
      return undefined;
    }

    // Only raise the global loading screen on the FIRST load for this user.
    // onAuthStateChanged can hand back a fresh User object for the SAME signed-in
    // user (token refresh, multi-tab propagation, re-auth); re-raising loading then
    // would blank the already-rendered app behind the spinner mid-session. When we
    // already have the user's info, the listeners below refresh it in place instead.
    // A cached profile paints immediately and its role starts the roles listener in parallel.
    const cachedProfile = readProfileCache(currentUser.uid);
    if (cachedProfile) {
      setUserInfo((prevUserInfo) => ({ ...cachedProfile.userInfo, ...prevUserInfo }));
      setRoles((prevRoles) => (prevRoles.length ? prevRoles : cachedProfile.roles));
      setLoading(false);
    } else if (Object.keys(userInfo).length === 0) {
      setLoading(true);
    }

    firestore.collection('userSettings').doc(currentUser.uid).get()
    .then((settingsDoc) => { setUserSettings(settingsDoc.data() || {}); setSettingsLoaded(true); })
    .catch((settingsError) => { console.warn(settingsError); setSettingsLoaded(true); });

    const unsubscribeUser = firestore
      .collection('users')
      .where('email', '==', currentUser.email)
      .onSnapshot((snapshot) => {
        const userDocument = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            role: data.role || 'Employee',
            firstName: data.firstName || data.displayName.substring(0, data.displayName.indexOf(' ')),
            lastName: data.lastName || data.displayName.substring(data.displayName.indexOf(' ') + 1),
          };
        })[0];

        if (!userDocument) {
          clearProfileCache(currentUser.uid);
          setUserInfo({});
          setRoles([]);
          setLoading(false);
          return;
        }

        setUserInfo((prevUserInfo) => ({ ...prevUserInfo, ...userDocument }));
      }, (userError) => {
        console.error('Error listening to user document:', userError);
        setLoading(false);
      });

    return () => unsubscribeUser();
    // userInfo is read only to gate the initial spinner above; it is intentionally
    // NOT a dependency — adding it would re-subscribe the user-doc listener on every
    // profile change.
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Live listener on the role the user currently holds. Keyed on the resolved
  // role title: when the user's role changes (above), this re-subscribes to the
  // new role's document so permission changes apply instantly.
  useEffect(() => {
    const role = userInfo?.role;
    if (!role) {
      return undefined;
    }

    const unsubscribeRoles = firestore
      .collection('roles')
      .where('title', '==', role)
      .onSnapshot((snapshot) => {
        const roleDocument = snapshot.docs.map((doc) => {
          const data = doc.data();
          return { permissions: data.permissions || [], managedDepartments: data.managedDepartments || [] };
        })[0];
        const permissions = roleDocument?.permissions || [];
        const managedDepartments = roleDocument?.managedDepartments || [];
        setRoles(permissions);
        setUserInfo((prevUserInfo) => ({ ...prevUserInfo, permissions, managedDepartments }));
        setRolesResolvedFor(role);
        setLoading(false);
      }, (rolesError) => {
        console.error('Error listening to roles:', rolesError);
        setLoading(false);
      });

    return () => unsubscribeRoles();
    // Must key on the role title ONLY — depending on the whole userInfo object
    // would re-subscribe on every permission update and loop.
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.role]);

  // Cache only once the live roles match the live role, so a stalled load never overwrites a good cache.
  useEffect(() => {
    if (!currentUser || !rolesResolvedFor || rolesResolvedFor !== userInfo?.role) return;
    writeProfileCache(currentUser.uid, userInfo, roles);
  }, [currentUser, rolesResolvedFor, userInfo, roles]);

  // Watchdog: never let the loading spinner persist on a stalled Firestore stream.
  // The user/role onSnapshot listeners have no client-side timeout, so a cold or
  // blocked WebChannel could otherwise gate first paint for minutes. The listeners
  // still populate userInfo/roles whenever they eventually arrive.
  useEffect(() => {
    if (!loading) return undefined;
    const t = setTimeout(() => { setLoading(false); setAuthLoading(false); }, 8000);
    return () => clearTimeout(t);
  }, [loading]);

  const value = useMemo(() => ({
    currentUser,
    userInfo,
    loading,
    authLoading,
    settingsLoaded,
    roles,
    login,
    signup,
    logout,
    resetPassword,
    updateEmail,
    updatePassword,
    updateUserSettings,
    userSettings,
  }), [currentUser, userInfo, loading, authLoading, settingsLoaded, roles, login, signup, logout, resetPassword, updateEmail, updatePassword, updateUserSettings, userSettings])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
